import React, { useState } from 'react';
import './signupcontent.css';

const API_URL = process.env.REACT_APP_API_URL;

const SignUpContent = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [signupstate, setSignUpState] = useState('nil');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const rules = {
        minLength: /.{8,}/,
        uppercase: /[A-Z]/,
        lowercase: /[a-z]/,
        number: /[0-9]/
    };
    const validatePassword = (pwd, confirmPwd = confirmPassword) => {
        let strength = 0;
        let valid = true;

        if (!rules.minLength.test(pwd)) valid = false;
        else strength += 1;
        if (!rules.uppercase.test(pwd)) valid = false;
        else strength += 1;
        if (!rules.lowercase.test(pwd)) valid = false;
        else strength += 1;
        if (!rules.number.test(pwd)) valid = false;
        else strength += 1;
        if (pwd !== confirmPwd) valid = false;
        else if (pwd || confirmPwd) strength += 1;

        setPasswordStrength(strength);
        setIsPasswordValid(valid);
    };

    const handlePasswordChange = (event) => {
        const newPassword = event.target.value;
        setPassword(newPassword);
        validatePassword(newPassword);
    };

    const handleConfirmPasswordChange = (event) => {
        const newConfirmPassword = event.target.value;
        setConfirmPassword(newConfirmPassword);
        validatePassword(password, newConfirmPassword);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!isPasswordValid || isSubmitting) return;

        // Set submitting state to true to prevent multiple submissions
        setIsSubmitting(true);

        const formData = new FormData(event.target);
        const formDataObj = Object.fromEntries(formData);
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formDataObj),
        };

        // Debug log to see what URL is being used
        console.log('API URL:', API_URL);
        console.log('Making request to:', `${API_URL}/signup`);
        console.log('Form data:', formDataObj);

        try {
            const response = await fetch(`${API_URL}/signup`, requestOptions);
            
            // Log the response status and headers
            console.log('Response status:', response.status);
            console.log('Response headers:', [...response.headers.entries()]);
            
            // Check if response is JSON
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                // If not JSON, get the text response for debugging
                const textResponse = await response.text();
                console.error('Non-JSON response:', textResponse);
                throw new Error('Server returned non-JSON response');
            }
            
            const data = await response.json();

            if (response.ok) {
                setSignUpState('success');
            } else {
                console.error('Error:', data.message);
                setSignUpState('error');
                // Reset submitting state if there's an error
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error('Error during signup:', error.message);
            // Add more detailed error information
            if (error.name === 'SyntaxError') {
                console.error('JSON parsing error - likely received HTML instead of JSON');
            }
            setSignUpState('error');
            // Reset submitting state if there's an error
            setIsSubmitting(false);
        }
    };

    return (
        <div className="signupbody">
            <div className="signupcontainer">

                <div className={signupstate === 'nil' ? 'signupdisplay' : 'nodisplay'}>
                    <div className="signuptitle">
                        <h2 style={{ fontWeight: '400' }}>Create an account</h2>
                        <p style={{ fontSize: 'small', fontWeight: '350', marginTop: '0.5rem', marginBottom: '0.5rem', textAlign: 'center' }}>Sign up to join NTU's largest project community!</p>
                    </div>
                    <form className='signupform' style={{ marginBottom: '2rem' }} onSubmit={handleSubmit}>
                        <div>
                            <input type='text' name='FirstName' className='signupforminput' placeholder='First Name' style={{ width: '48.5%', marginRight: '3%' }} required />
                            <input type='text' name='LastName' className='signupforminput' placeholder='Last Name' style={{ width: '48.5%' }} required />
                        </div>
                        <input type='text' name='Username' className='signupforminput' placeholder='Username' required />
                        <input type='text' name='Email' className='signupforminput' placeholder='Email' required />
                        <input
                            type="password"
                            name="Password"
                            className="signupforminput"
                            placeholder="Password"
                            onChange={handlePasswordChange}
                            required
                            pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$"
                            onInvalid={(e) => e.target.setCustomValidity("Password must be at least 8 characters long and include an uppercase letter, lowercase letter, and number.")}
                            onInput={(e) => e.target.setCustomValidity('')}
                        />
                        <input
                            type="password"
                            className="signupforminput"
                            placeholder="Re-enter Password"
                            onChange={handleConfirmPasswordChange}
                            required
                            onInvalid={(e) => e.target.setCustomValidity("Please confirm your password.")}
                            onInput={(e) => e.target.setCustomValidity('')}
                        />
                        <div className="password-strength">
                            <div className={`strength-bar strength-${passwordStrength}`}></div>
                        </div>
                        <ul className="password-checklist">
                            <li className={password.length >= 8 ? 'valid' : ''}>At least 8 characters</li>
                            <li className={rules.uppercase.test(password) ? 'valid' : ''}>At least one uppercase letter</li>
                            <li className={rules.lowercase.test(password) ? 'valid' : ''}>At least one lowercase letter</li>
                            <li className={rules.number.test(password) ? 'valid' : ''}>At least one number</li>
                            <li className={password && confirmPassword && password === confirmPassword ? 'valid' : ''}>Passwords match</li>
                        </ul>

                        <div style={{ display: 'flex' }}>
                            <input type='checkbox' id='tandc' name='tandc' value='tandc' style={{ marginBottom: '1.3rem', marginRight: '0.5rem' }} required />
                            <label htmlFor='tandc'>
                                <p style={{ fontWeight: '300', fontSize: 'small' }}>
                                    I agree to the <a href="/test" className='signuplinks'>Terms and Conditions</a> and understand the <a href="/test" className='signuplinks'>Privacy Policy</a>
                                </p>
                            </label>
                        </div>
                        <input 
                            type='submit' 
                            className='signupforminput submitform' 
                            value={isSubmitting ? 'Creating Account...' : 'Create Account'}
                            disabled={isSubmitting}
                            style={{
                                opacity: isSubmitting ? 0.7 : 1,
                                cursor: isSubmitting ? 'not-allowed' : 'pointer'
                            }}
                        />
                    </form>
                    <div className='existingaccount'>
                        <hr />
                        <center><span style={{ display: 'inline-block', margin: '0.3rem', textAlign: 'center' }}>Already have an account ?
                            <a href='/login' className='signuplinks' style={{ marginLeft: '0.3rem' }}> Log in</a></span></center>
                        <hr />
                    </div>
                </div>

                <div className={signupstate === 'success' || 'error' ? 'signupresultsdisplay' : 'nodisplay'}>
                    <div className={signupstate === 'success' ? 'signupresults' : 'nodisplay'}>
                        <div className='signup1'>Your sign up was successfully.</div>
                        <div className='signup2'>Please click <a href='/login' style={{ cursor: 'pointer' }}>here</a> to login.</div>
                    </div>
                    <div className={signupstate === 'error' ? 'signupresults' : 'nodisplay'}>
                        <div className='signup1'>An error has occured.</div>
                        <div className='signup2'>Please sign up again by clicking <a href='/signup'>here</a>.</div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default SignUpContent;
